import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "client",
    clinician_code: (params.get("code") || "").toUpperCase(),
    consent: false,
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.role !== "client") delete payload.clinician_code;
      const u = await register(payload);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "clinician" ? "/clinician" : "/welcome/ai");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="warm-card p-8">
        <h1 className="font-display text-3xl font-extrabold text-stone-900 mb-1">Create account</h1>
        <p className="text-sm text-stone-600 mb-6">Small step, big shift.</p>

        <div className="flex gap-2 mb-5 p-1 bg-stone-100 rounded-full">
          {["client", "clinician"].map((r) => (
            <button
              type="button"
              key={r}
              data-testid={`role-toggle-${r}`}
              onClick={() => setForm({ ...form, role: r })}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition ${
                form.role === r ? "bg-white shadow text-stone-900" : "text-stone-500"
              }`}
            >
              {r === "client" ? "I'm here for my own habits" : "I'm here to support others"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-600">Name</label>
            <input
              data-testid="register-name"
              required
              value={form.name}
              onChange={upd("name")}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-600">Email</label>
            <input
              data-testid="register-email"
              type="email"
              required
              value={form.email}
              onChange={upd("email")}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-600">Password</label>
            <input
              data-testid="register-password"
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={upd("password")}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition"
            />
            <div className="text-xs text-stone-500 mt-1">At least 8 characters.</div>
          </div>
          {form.role === "client" && (
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-600">
                Coach code (optional)
              </label>
              <input
                data-testid="register-clinician-code"
                value={form.clinician_code}
                onChange={upd("clinician_code")}
                placeholder="e.g. A3F9C2"
                className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 uppercase font-mono focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition"
              />
              <div className="text-xs text-stone-500 mt-1">Link your account to your coach.</div>
            </div>
          )}
          <label className="flex items-start gap-2 text-xs text-stone-600 leading-relaxed" data-testid="register-consent-label">
            <input
              type="checkbox"
              data-testid="register-consent"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-0.5 w-4 h-4 accent-amber-600 shrink-0"
            />
            <span>
              I've read the{" "}
              <Link to="/privacy" target="_blank" className="text-amber-800 font-semibold hover:underline" data-testid="register-privacy-link">Privacy Notice</Link>{" "}
              and understand Root & Rose is a wellness app for tending habits — not therapy, diagnosis, treatment or emergency care. If you are in crisis, call or text 988 (US). AI features are optional, de-identified, and never a substitute for a person.
            </span>
          </label>
          {err && (
            <div data-testid="register-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
          <button data-testid="register-submit" type="submit" disabled={loading || !form.consent} className="amber-btn w-full disabled:opacity-50">
            {loading ? "Creating…" : "Create my account"}
          </button>
        </form>
        <div className="text-sm text-stone-500 text-center mt-6">
          Have an account?{" "}
          <Link to="/login" data-testid="register-to-login" className="text-amber-800 font-semibold hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
